
import React from 'react';
import PageLayout from '@/components/layout/PageLayout';
import PageHero from '@/components/ui/PageHero';
import ScrollReveal from '@/components/ui/ScrollReveal';

const PrivacyPolicy = () => {
  // Get current date for the effective date
  const currentDate = new Date();
  const formattedDate = `${currentDate.toLocaleString('default', { month: 'long' })} ${currentDate.getDate()}, ${currentDate.getFullYear()}`;

  return (
    <PageLayout
      title="Privacy Policy | Astrem"
      description="Astrem's privacy policy outlines how we collect, use, and protect your personal information."
      ogImage="https://astrem.co/lovable-uploads/7850aff3-fafb-483a-9dd4-68c25ac786e2.png"
    >
      <PageHero
        title="Privacy Policy"
        subtitle="Data Privacy Standards & Regulations"
        description="At Astrem, we are committed to protecting the privacy of our website visitors and customers."
        backgroundImage="https://images.unsplash.com/photo-1557597774-9d273605dfa9?q=80&w=2070&auto=format&fit=crop"
      />

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <ScrollReveal>
              <div className="prose prose-lg max-w-none">
                <p className="text-lg font-medium">Effective Date: {formattedDate}</p>
                
                <p>At Astrem, we are committed to protecting the privacy of our website visitors and customers. This Privacy Policy explains how we collect, use, and share your personal information.</p>
                
                <h2 className="text-2xl font-medium mt-8 mb-4">Information We Collect</h2>
                
                <p>When you visit our website, we automatically collect certain information about your device, including your IP address, browser type, operating system, referring website, pages viewed, time spent on our website, and other usage data. We use meta-pixels and Google Analytics to collect this information, which helps us understand how visitors interact with our website and improve its performance. We also use cookies when you visit our site (see our Cookie Policy below).</p>
                
                <p>We also collect personal information that you voluntarily provide to us, such as your name, email address, phone number, and any other information you choose to share. This may occur when you:</p>
                
                <ul className="list-disc ml-6 my-4">
                  <li>Submit a contact form on our website</li>
                  <li>Contact us via email</li>
                  <li>Contact us via SMS</li>
                  <li>Interact with our Meta Ads Instant Forms</li>
                  <li>Participate in an online quiz from one of our marketing campaigns</li>
                  <li>Sign up for a giveaway or raffle</li>
                  <li>Become a client of Astrem</li>
                </ul>
                
                <h2 className="text-2xl font-medium mt-8 mb-4">How We Use Your Information</h2>
                
                <p>We use the information we collect to:</p>
                
                <ul className="list-disc ml-6 my-4">
                  <li>Provide and improve our website and services</li>
                  <li>Personalize your experience on our website</li>
                  <li>Respond to your inquiries and requests</li>
                  <li>Communicate with you about orders, services, or information you have requested</li>
                  <li>Send you marketing communications, including newsletters and promotional offers, if you have opted in to receive them. We aim to keep such communications minimal and relevant.</li>
                  <li>Retarget you with paid advertisements on various platforms.</li>
                  <li>Analyze website usage and trends</li>
                </ul>
                
                <h2 className="text-2xl font-medium mt-8 mb-4">Opting In and Out of Communications</h2>
                
                <p>You may opt-in to receive marketing communications from us through the following methods:</p>
                
                <ul className="list-disc ml-6 my-4">
                  <li>Meta Ads Instant Form</li>
                  <li>Submitting a contact form on our website</li>
                  <li>Emailing us</li>
                  <li>Sending us an SMS</li>
                  <li>Using an online quiz from one of our marketing campaigns</li>
                  <li>Signing up for a giveaway or raffle</li>
                  <li>Becoming a client of ours</li>
                </ul>
                
                <p>If you no longer wish to receive marketing communications from us, you can easily opt-out at any time. Simply reply "STOP" to any SMS message you receive from us, or email us at admin@astrem.co with the subject line "Unsubscribe." We will promptly remove you from our mailing list.</p>
                
                <h2 className="text-2xl font-medium mt-8 mb-4">Information Sharing</h2>
                
                <p>We do not sell or rent your personal information to third parties. We share information with the following service providers:</p>
                
                <ul className="list-disc ml-6 my-4">
                  <li>Google Analytics: For website analytics and understanding user behavior.</li>
                  <li>Meta: For advertising and retargeting.</li>
                </ul>
                
                <p>These service providers are contractually obligated to protect your information and only use it for the purposes we specify.</p>
                
                <h2 className="text-2xl font-medium mt-8 mb-4">Data Retention</h2>
                
                <p>We retain your contact information for as long as you are a client and for a reasonable period thereafter for marketing purposes, unless you opt-out.</p>
                
                <h2 className="text-2xl font-medium mt-8 mb-4">Children's Privacy</h2>
                
                <p>Our website and services are not directed at children under the age of 13. We do not knowingly collect personal information from children under 13. If you become aware that a child has provided us with personal information, please contact us immediately.</p>
                
                <h2 className="text-2xl font-medium mt-8 mb-4">Changes to this Privacy Policy</h2>
                
                <p>We may update this Privacy Policy from time to time. Any changes will be posted on this page with a revised "Effective Date." We encourage you to review this Privacy Policy periodically.</p>
                
                <h2 className="text-2xl font-medium mt-8 mb-4">Contact Us</h2>
                
                <p>If you have any questions about this Privacy Policy, please contact us at:</p>
                
                <p>
                  Astrem<br />
                  admin@astrem.co
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default PrivacyPolicy;
